import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, PauseCircle, Loader2, Settings2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useOrg } from '@/contexts/OrgContext'
import { useEngineStatus, deriveEngineState } from './engine-status'

interface EngineStatusBannerProps {
  // When true, render a compact inline badge instead of the full banner (used
  // next to per-action controls, e.g. the hold-approval buttons).
  compact?: boolean
  className?: string
  // Hide the "Administrar" link (e.g. when already on the control page).
  hideManageLink?: boolean
}

// ─── Reusable engine-state banner ────────────────────────────────────────────
// Surfaces the outreach engine's REAL state anywhere it matters (OutreachActivity
// header, hold-approval actions). Makes the month-long silent OFF (CA-010)
// visible without letting the UI pretend outreach is running.
export function EngineStatusBanner({ compact, className, hideManageLink }: EngineStatusBannerProps) {
  const { orgId } = useOrg()
  const { data: status, isLoading, error } = useEngineStatus(orgId)
  const state = deriveEngineState(status)

  if (isLoading) {
    return (
      <div className={`flex items-center gap-1.5 text-xs text-muted-foreground ${className ?? ''}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Verificando estado del motor…
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 ${className ?? ''}`}>
        <AlertTriangle className="h-3.5 w-3.5" />
        No se pudo leer el estado del motor de outreach
      </div>
    )
  }

  // ── Compact badge (for inline placement next to actions) ──────────────────
  if (compact) {
    if (state === 'on') {
      return (
        <Badge className={`bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 gap-1 ${className ?? ''}`}>
          <CheckCircle2 className="h-3 w-3" /> Motor ON
        </Badge>
      )
    }
    if (state === 'enabled_no_cron') {
      return (
        <Badge className={`bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 gap-1 ${className ?? ''}`}>
          <AlertTriangle className="h-3 w-3" /> Habilitado, sin ejecutor
        </Badge>
      )
    }
    return (
      <Badge className={`bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300 gap-1 ${className ?? ''}`}>
        <PauseCircle className="h-3 w-3" /> Motor OFF
      </Badge>
    )
  }

  // ── Full banner ────────────────────────────────────────────────────────────
  const orphanCount = status?.orphan_scheduled_count ?? 0

  const tone =
    state === 'on'
      ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200'
      : state === 'enabled_no_cron'
        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
        : 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200'

  const Icon = state === 'on' ? CheckCircle2 : state === 'enabled_no_cron' ? AlertTriangle : PauseCircle

  const headline =
    state === 'on'
      ? 'El motor de outreach está encendido'
      : state === 'enabled_no_cron'
        ? 'Outreach habilitado, pero no hay ejecutor'
        : 'El motor de outreach está apagado'

  const detail =
    state === 'on'
      ? 'Los envíos programados se ejecutarán en cada ciclo.'
      : state === 'enabled_no_cron'
        ? 'El kill switch está encendido pero no existe un cron que procese la cola — nada se envía todavía.'
        : orphanCount > 0
          ? `Ningún envío se ejecuta mientras esté apagado. Hay ${orphanCount.toLocaleString('es-MX')} en cola esperando.`
          : 'Ningún envío se ejecuta mientras esté apagado. Los pasos se acumulan en la cola.'

  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${tone} ${className ?? ''}`}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{headline}</p>
        <p className="text-xs opacity-90 mt-0.5">{detail}</p>
      </div>
      {!hideManageLink && (
        <Button asChild variant="outline" size="sm" className="h-7 shrink-0 text-xs">
          <Link to="/settings/outreach">
            <Settings2 className="h-3.5 w-3.5 mr-1" />
            Administrar
          </Link>
        </Button>
      )}
    </div>
  )
}
